/**
 * Platform "me" lane contracts (design 5.3.1, platform discriminant:
 * entitlement: null on every endpoint — same decided-free gate decision as
 * the legacy '/api/me/' UNGATED_PREFIXES entry, now structural). The worker
 * registers them through the transitional platform module
 * (worker/src/features/me/api.ts, reason per registration), delegating to
 * the legacy me-routines handler.
 *
 * Current consumers: the routines feature's personal-scope page (model
 * picker defaults), the legacy PersonalRoutineModelCard (raw literals
 * under the warn baseline until it migrates), and the rightsizing
 * feature's PersonalRightsizingModelCard (personal model override).
 */

import { defineContracts, endpoint } from '../featureModule'
import { identity } from './me'

export const contracts = defineContracts(identity, {
  /** GET /api/users/me — the operator's own identity/impersonation bootstrap lane
   *  (decided-free; served by the users-owned handler through this null-entitlement
   *  `me` module — see worker features/me/api.ts). Consumed only by platform-side
   *  code (PermissionContext, TimezoneContext, and the impersonation banner). */
  usersMe: endpoint('GET', '/api/users/me', { entitlement: null }),
  /** GET /api/me/routines/default-model — the operator's personal routine model default. */
  getRoutineDefaultModel: endpoint('GET', '/api/me/routines/default-model', { entitlement: null }),
  /** PUT /api/me/routines/default-model — set the personal routine model default. */
  updateRoutineDefaultModel: endpoint('PUT', '/api/me/routines/default-model', { entitlement: null }),
  /** GET /api/me/rightsizing/default-model — the operator's personal rightsizing model override. */
  getRightsizingDefaultModel: endpoint('GET', '/api/me/rightsizing/default-model', { entitlement: null }),
  /** PUT /api/me/rightsizing/default-model — set the personal rightsizing model override. */
  updateRightsizingDefaultModel: endpoint('PUT', '/api/me/rightsizing/default-model', { entitlement: null }),
  /** PUT /api/me/mcp-views — the person's "Show interactive views in chat" switch: { enabled } → { enabled, allowedByGovernor }. */
  updateMcpViews: endpoint('PUT', '/api/me/mcp-views', { entitlement: null }),
  /** GET /api/me/browser/keep-logins — the person's "Keep my logins across cases" switch: → { keepLogins, savedAt }. */
  getBrowserKeepLogins: endpoint('GET', '/api/me/browser/keep-logins', { entitlement: null }),
  /** PUT /api/me/browser/keep-logins — { keepLogins } → { keepLogins, savedAt }; off deletes the saved logins first. */
  updateBrowserKeepLogins: endpoint('PUT', '/api/me/browser/keep-logins', { entitlement: null }),
  /** GET /api/me/duo-approvals — Duo for approvals and the person's own choice: → { policy, choice, duoOn }. */
  getDuoApprovals: endpoint('GET', '/api/me/duo-approvals', { entitlement: null }),
  /** PUT /api/me/duo-approvals — { duoOn } → { policy, choice, duoOn }; only while Duo for approvals is Optional. */
  updateDuoApprovals: endpoint('PUT', '/api/me/duo-approvals', { entitlement: null }),

  /** POST /api/me/assistant/ask — Guide: { question, currentPath (with its query), destinations, history, guide?, guideStep?: { index: number | null, done: boolean[] } (the Guide panel's step and the steps' live done state) } → { answer, destinationIds, handoff?: 'investigate', knowledgeError? }. */
  askNavigationAssistant: endpoint('POST', '/api/me/assistant/ask', { entitlement: null }),
  /** GET /api/me/guides — the official guides (Guide's tours) this console applied, only those the person may use: { version, guides }. */
  getGuides: endpoint('GET', '/api/me/guides', { entitlement: null }),

  // ---- Per-operator AI identity (AgentIdentityCard). Served by the me-owned
  //      agent-identity lane; create/remove are human-only on the worker. ----
  /** GET /api/me/agent-identity — { enabled, agents, areas, areasError? }. */
  getAgentIdentity: endpoint('GET', '/api/me/agent-identity', { entitlement: null }),
  /** POST /api/me/agent-identity — set up the person's agent in each signed-in area ({ agentAssertions }). */
  createAgentIdentity: endpoint('POST', '/api/me/agent-identity', { entitlement: null }),
  /** DELETE /api/me/agent-identity — remove the person's agent in each signed-in area ({ agentAssertions }). */
  removeAgentIdentity: endpoint('DELETE', '/api/me/agent-identity', { entitlement: null }),

  // ---- Per-operator BYO-AI Claude seat OAuth (ClaudeSeatCard). The handler is
  //      ai_providers-owned but the '/api/me/claude-oauth' lane is decided-free
  //      and SERVES from the null-entitlement `me` module. ----
  /** GET /api/me/claude-oauth/status — is the operator's Claude seat connected? */
  getClaudeSeatStatus: endpoint('GET', '/api/me/claude-oauth/status', { entitlement: null }),
  /** POST /api/me/claude-oauth/authorize — begin the PKCE consent flow (returns auth_url). */
  authorizeClaudeSeat: endpoint('POST', '/api/me/claude-oauth/authorize', { entitlement: null }),
  /** POST /api/me/claude-oauth/exchange — exchange the pasted code for stored creds. */
  exchangeClaudeSeat: endpoint('POST', '/api/me/claude-oauth/exchange', { entitlement: null }),
  /** POST /api/me/claude-oauth/validate — validate the seat and its AI Runner transport. */
  validateClaudeSeat: endpoint('POST', '/api/me/claude-oauth/validate', { entitlement: null }),
  /** GET /api/me/claude-oauth/usage — the seat's plan usage windows. */
  getClaudeSeatUsage: endpoint('GET', '/api/me/claude-oauth/usage', { entitlement: null }),
  /** DELETE /api/me/claude-oauth — disconnect the operator's Claude seat. */
  disconnectClaudeSeat: endpoint('DELETE', '/api/me/claude-oauth', { entitlement: null }),

  // ---- Per-operator BYO-AI ChatGPT seat OAuth (OpenAISeatCard). ----
  /** GET /api/me/openai-oauth/status — is the operator's ChatGPT seat connected? */
  getOpenAISeatStatus: endpoint('GET', '/api/me/openai-oauth/status', { entitlement: null }),
  /** POST /api/me/openai-oauth/device-code — start the device-login flow. */
  startOpenAISeatDeviceCode: endpoint('POST', '/api/me/openai-oauth/device-code', { entitlement: null }),
  /** POST /api/me/openai-oauth/token — poll for device-login completion. */
  pollOpenAISeatToken: endpoint('POST', '/api/me/openai-oauth/token', { entitlement: null }),
  /** POST /api/me/openai-oauth/validate — validate the seat + list models. */
  validateOpenAISeat: endpoint('POST', '/api/me/openai-oauth/validate', { entitlement: null }),
  /** GET /api/me/openai-oauth/usage — the seat's plan usage windows. */
  getOpenAISeatUsage: endpoint('GET', '/api/me/openai-oauth/usage', { entitlement: null }),
  /** DELETE /api/me/openai-oauth — disconnect the operator's ChatGPT seat. */
  disconnectOpenAISeat: endpoint('DELETE', '/api/me/openai-oauth', { entitlement: null }),

  // ---- Per-operator muse enrollment (MuseCard). The handler is
  //      ai_providers-owned but the '/api/me/muse' lane is decided-free
  //      and SERVES from the null-entitlement `me` module. ----
  /** GET /api/me/muse/status — is muse connected (endpoint row + login token)? */
  getMuseStatus: endpoint('GET', '/api/me/muse/status', { entitlement: null }),
  /** POST /api/me/muse/device-code — start the Meta device flow. */
  startMuseDeviceCode: endpoint('POST', '/api/me/muse/device-code', { entitlement: null }),
  /** POST /api/me/muse/token — poll device completion; mints and stores on approval. */
  pollMuseToken: endpoint('POST', '/api/me/muse/token', { entitlement: null }),
  /** POST /api/me/muse/validate — validate the login token + endpoint models. */
  validateMuse: endpoint('POST', '/api/me/muse/validate', { entitlement: null }),
  /** GET /api/me/muse/usage — live subscription usage off the login token. */
  getMuseUsage: endpoint('GET', '/api/me/muse/usage', { entitlement: null }),
  /** DELETE /api/me/muse — remove the endpoint row AND the login token together. */
  disconnectMuse: endpoint('DELETE', '/api/me/muse', { entitlement: null }),

  // ---- The three AI seats in one read (My settings → AI accounts). Served by
  //      the ai_providers-owned operatorAiSeats handler in the null-entitlement
  //      `me` module. Status only; usage stays per-seat. ----
  /** GET /api/me/ai-seats — { claude: { connected }, openai: { connected }, muse: { connected, endpoint, models } }. */
  getAiSeatUsage: endpoint('GET', '/api/me/ai-seats/usage', { entitlement: null }),
  getAiSeats: endpoint('GET', '/api/me/ai-seats', { entitlement: null }),

  // ---- Per-operator AI-runner personal workspace / packages / secrets
  //      (PersonalAiRunnerCard). Decided-free lane, SERVES from `me`. ----
  /** GET /api/me/ai-runner/workspace — personal AI-runner workspace + packages + secrets. */
  getAiRunnerWorkspace: endpoint('GET', '/api/me/ai-runner/workspace', { entitlement: null }),
  /** GET /api/me/ai-runner/memory — live workspace runner memory only. */
  getAiRunnerMemoryUsage: endpoint('GET', '/api/me/ai-runner/memory', { entitlement: null }),
  /** PUT /api/me/ai-runner/workspace — update mode, memory limit, and runtime. */
  updateAiRunnerWorkspace: endpoint('PUT', '/api/me/ai-runner/workspace', { entitlement: null }),
  /** POST /api/me/ai-runner/workspace/:action — restart|reset the workspace. */
  runAiRunnerWorkspaceAction: endpoint('POST', '/api/me/ai-runner/workspace/:action', { entitlement: null }),
  /** POST /api/me/ai-runner/packages — add a package. */
  createAiRunnerPackage: endpoint('POST', '/api/me/ai-runner/packages', { entitlement: null }),
  /** DELETE /api/me/ai-runner/packages/:id — remove a package. */
  deleteAiRunnerPackage: endpoint('DELETE', '/api/me/ai-runner/packages/:id', { entitlement: null }),
  /** POST /api/me/ai-runner/packages/:id/retry — retry a failed package install. */
  retryAiRunnerPackage: endpoint('POST', '/api/me/ai-runner/packages/:id/retry', { entitlement: null }),
  /** PUT /api/me/ai-runner/secrets/:name — create/update a runner secret. */
  updateAiRunnerSecret: endpoint('PUT', '/api/me/ai-runner/secrets/:name', { entitlement: null }),
  /** PATCH /api/me/ai-runner/secrets/:name — enable/disable a saved runner secret. */
  setAiRunnerSecretEnabled: endpoint('PATCH', '/api/me/ai-runner/secrets/:name', { entitlement: null }),
  /** DELETE /api/me/ai-runner/secrets/:name — remove a runner secret. */
  deleteAiRunnerSecret: endpoint('DELETE', '/api/me/ai-runner/secrets/:name', { entitlement: null }),

  // ---- Per-operator Anthropic-compatible model endpoints
  //      (PersonalAiEndpointsCard). Decided-free lane, SERVES from `me`; the
  //      handler is ai_providers-owned (operatorEndpoints.ts). ----
  /** GET /api/me/ai-endpoints — the operator's own endpoints (never their keys). */
  getAiEndpoints: endpoint('GET', '/api/me/ai-endpoints', { entitlement: null }),
  /** POST /api/me/ai-endpoints — add an endpoint; validated before it is stored. */
  createAiEndpoint: endpoint('POST', '/api/me/ai-endpoints', { entitlement: null }),
  /** PUT /api/me/ai-endpoints/:id — update label, base URL, key or auth style. */
  updateAiEndpoint: endpoint('PUT', '/api/me/ai-endpoints/:id', { entitlement: null }),
  /** POST /api/me/ai-endpoints/:id/validate — re-run the endpoint's own model query. */
  validateAiEndpoint: endpoint('POST', '/api/me/ai-endpoints/:id/validate', { entitlement: null }),
  /** DELETE /api/me/ai-endpoints/:id — remove an endpoint. */
  deleteAiEndpoint: endpoint('DELETE', '/api/me/ai-endpoints/:id', { entitlement: null }),

  // ---- BYO integration credentials (docs/byo-integration-credentials-brief.md).
  //      Personal credentials for the customer's outbound integrations, layered
  //      over the tenant service credential. Decided-free `me` lane. ----
  /** GET /api/me/integration-credentials — descriptors + this operator's connection status. */
  validateMicrosoftPermissions: endpoint('POST', '/api/me/microsoft-permissions/validate', { entitlement: null }),
  getIntegrationCredentials: endpoint('GET', '/api/me/integration-credentials', { entitlement: null }),
  /** POST /api/me/integration-credentials — connect (store) a personal credential for one instance. */
  connectIntegrationCredential: endpoint('POST', '/api/me/integration-credentials', { entitlement: null }),
  /** DELETE /api/me/integration-credentials/:integration/:instanceId — disconnect one. */
  deleteIntegrationCredential: endpoint('DELETE', '/api/me/integration-credentials/:integration/:instanceId', { entitlement: null }),
})
